import { useCallback, useEffect, useRef, useState } from "react";
import type React from "react";
import { tokens } from "@/design/tokens";

// ─── Public types ────────────────────────────────────────────────────────────

export type DockSlot = "left-rail" | "right-rail" | "floating";

export interface PanelPosition {
  x: number;
  y: number;
}

export interface DockablePanelState {
  slot: DockSlot;
  position: PanelPosition | null;
  width: number;
  height: number;
  minimized: boolean;
}

export interface UseDockablePanelOptions {
  storageKey: string;
  defaultSlot: DockSlot;
  defaultWidth: number;
  defaultHeight: number;
  railWidth: number;
  snapRadius?: number;
}

export interface UseDockablePanelReturn {
  state: DockablePanelState;
  onDragHandlePointerDown: (e: React.PointerEvent) => void;
  snapHome: () => void;
  toggleMinimized: () => void;
  panelStyle: React.CSSProperties;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function buildDefaultState(options: UseDockablePanelOptions): DockablePanelState {
  return {
    slot: options.defaultSlot,
    position: null,
    width: options.defaultWidth,
    height: options.defaultHeight,
    minimized: false,
  };
}

function loadState(key: string, fallback: DockablePanelState): DockablePanelState {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return fallback;
    const obj = parsed as Record<string, unknown>;
    if (
      (obj.slot === "left-rail" || obj.slot === "right-rail" || obj.slot === "floating") &&
      typeof obj.width === "number" &&
      typeof obj.height === "number" &&
      typeof obj.minimized === "boolean"
    ) {
      return {
        slot: obj.slot,
        position: isValidPosition(obj.position) ? obj.position : null,
        width: obj.width,
        height: obj.height,
        minimized: obj.minimized,
      };
    }
  } catch {
    /* corrupt storage — fall through */
  }
  return fallback;
}

function isValidPosition(val: unknown): val is PanelPosition {
  return (
    typeof val === "object" &&
    val !== null &&
    typeof (val as Record<string, unknown>).x === "number" &&
    typeof (val as Record<string, unknown>).y === "number"
  );
}

type SnapZoneResult =
  | { slot: DockSlot; position: PanelPosition | null }
  | null;

function detectSnapZone(
  x: number,
  y: number,
  snapRadius: number,
  railWidth: number,
): SnapZoneResult {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  if (x <= snapRadius && y <= snapRadius) {
    return { slot: "floating", position: { x: 0, y: 0 } };
  }
  if (x >= vw - snapRadius && y <= snapRadius) {
    return { slot: "floating", position: { x: vw - railWidth, y: 0 } };
  }
  if (x <= snapRadius && y >= vh - snapRadius) {
    return { slot: "floating", position: { x: 0, y: vh - 400 } };
  }
  if (x >= vw - snapRadius && y >= vh - snapRadius) {
    return { slot: "floating", position: { x: vw - railWidth, y: vh - 400 } };
  }
  if (x >= vw / 2 - snapRadius && x <= vw / 2 + snapRadius && y <= snapRadius) {
    return { slot: "floating", position: { x: vw / 2 - railWidth / 2, y: 0 } };
  }
  if (x >= vw / 2 - snapRadius && x <= vw / 2 + snapRadius && y >= vh - snapRadius) {
    return { slot: "floating", position: { x: vw / 2 - railWidth / 2, y: vh - 400 } };
  }

  if (x <= railWidth + snapRadius) {
    return { slot: "left-rail", position: null };
  }
  if (x >= vw - railWidth - snapRadius) {
    return { slot: "right-rail", position: null };
  }

  return null;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

const DEFAULT_SNAP_RADIUS = 48;

export function useDockablePanel(options: UseDockablePanelOptions): UseDockablePanelReturn {
  const {
    storageKey,
    defaultSlot,
    defaultWidth,
    defaultHeight,
    railWidth,
    snapRadius = DEFAULT_SNAP_RADIUS,
  } = options;

  const [state, setState] = useState<DockablePanelState>(() =>
    loadState(storageKey, buildDefaultState(options)),
  );

  // Debounced persistence
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (saveTimer.current !== null) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      try {
        localStorage.setItem(storageKey, JSON.stringify(state));
      } catch {
        /* quota exceeded — silently drop */
      }
    }, 300);
    return () => {
      if (saveTimer.current !== null) clearTimeout(saveTimer.current);
    };
  }, [state, storageKey]);

  // Track drag offset to avoid panel jump on grab
  const dragOffset = useRef<{ dx: number; dy: number }>({ dx: 0, dy: 0 });
  const isDragging = useRef(false);
  const panelRef = useRef<HTMLElement | null>(null);

  const onDragHandlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      const target = e.currentTarget as HTMLElement;
      target.setPointerCapture(e.pointerId);
      isDragging.current = true;

      // Compute initial offset from panel origin to pointer
      const panelEl = target.closest("[data-dockable-panel]") as HTMLElement | null;
      panelRef.current = panelEl;
      if (panelEl) {
        panelEl.style.pointerEvents = "none";
      }

      const rect = panelEl?.getBoundingClientRect();
      const startX = rect ? rect.left : e.clientX;
      const startY = rect ? rect.top : e.clientY;
      dragOffset.current = {
        dx: e.clientX - startX,
        dy: e.clientY - startY,
      };

      // If currently docked, undock into floating at pointer position
      if (state.slot !== "floating") {
        setState((prev) => ({
          ...prev,
          slot: "floating",
          position: {
            x: e.clientX - dragOffset.current.dx,
            y: e.clientY - dragOffset.current.dy,
          },
        }));
      }

      const handleMove = (ev: PointerEvent) => {
        if (!isDragging.current) return;
        const newX = ev.clientX - dragOffset.current.dx;
        const newY = ev.clientY - dragOffset.current.dy;
        setState((prev) => ({
          ...prev,
          slot: "floating",
          position: { x: newX, y: newY },
        }));
      };

      const handleUp = (ev: PointerEvent) => {
        isDragging.current = false;
        target.releasePointerCapture(ev.pointerId);
        if (panelRef.current) {
          panelRef.current.style.pointerEvents = "";
        }
        document.removeEventListener("pointermove", handleMove);
        document.removeEventListener("pointerup", handleUp);

        const dropX = ev.clientX;
        const dropY = ev.clientY;
        const snap = detectSnapZone(dropX, dropY, snapRadius, railWidth);
        if (snap) {
          setState((prev) => ({
            ...prev,
            slot: snap.slot,
            position: snap.position,
            width: snap.slot !== "floating" ? railWidth : prev.width,
          }));
        }
      };

      document.addEventListener("pointermove", handleMove);
      document.addEventListener("pointerup", handleUp);
    },
    [state.slot, snapRadius, railWidth],
  );

  const snapHome = useCallback(() => {
    setState({
      slot: defaultSlot,
      position: null,
      width: defaultWidth,
      height: defaultHeight,
      minimized: false,
    });
    try {
      localStorage.removeItem(storageKey);
    } catch {
      /* ignore */
    }
  }, [defaultSlot, defaultWidth, defaultHeight, storageKey]);

  const toggleMinimized = useCallback(() => {
    setState((prev) => ({ ...prev, minimized: !prev.minimized }));
  }, []);

  const panelStyle: React.CSSProperties =
    state.slot === "floating" && state.position
      ? {
          position: "fixed",
          left: state.position.x,
          top: state.position.y,
          width: state.width,
          zIndex: tokens.zIndex.dock + 5,
          boxShadow: tokens.elevation[3],
          borderRadius: tokens.radius.md,
          transition: isDragging.current
            ? "none"
            : `left ${tokens.motion.duration.normal}ms ${tokens.motion.easing.standard}, top ${tokens.motion.duration.normal}ms ${tokens.motion.easing.standard}`,
        }
      : {};

  return {
    state,
    onDragHandlePointerDown,
    snapHome,
    toggleMinimized,
    panelStyle,
  };
}
